<template>
  <div class="comments-plugin">
    <popup
      :title="$t('plugins.comments.creationDialog.title')"
      ref="commentCreationDialog"
      class="comments-plugin__creation-dialog"
    >
      <r-form ref="popupForm"></r-form>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.commentCreationDialog.close()">{{ $t('btn.cancel') }}</btn>
        <btn icon="save" @click="createReaction" color="green">{{ $t('btn.create') }}</btn>
      </template>
    </popup>

    <popup
      :title="$t('plugins.comments.deleteDialog.title')"
      ref="commentDeleteDialog"
      v-if="selected"
    >
      <p>
        {{ $t('plugins.comments.deleteDialog.text') }}
        <b>"{{ selected.name }}"</b>?
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.commentDeleteDialog.close()">{{ $t('btn.cancel') }}</btn>
        <btn icon="trash" @click="deleteReaction" color="red">{{ $t('btn.delete') }}</btn>
      </template>
    </popup>

    <div class="comments-plugin__sidebar">
      <div class="comments-plugin__items">
        <div class="comments-plugin__item" v-for="(item, index) in reactions" :key="index">
          <short-card
            v-model="reactions[index]"
            @selected="setSelected(item)"
            :isSelected="isSelected(item)"
          >
            <div>{{ item.name }}</div>
            <div>{{ $store.getters.flowBlock(item.targetUuid).name }}</div>
          </short-card>
        </div>
      </div>
      <div
        class="comments-plugin__create"
        @click="$refs.commentCreationDialog.open()"
      >{{ $t('btn.create') }}</div>
      <div class="comments-plugin__sidebar-menu"></div>
    </div>
    <div class="comments-plugin__editor editor">
      <div v-if="selected" class="comments-plugin__top-buttons">
        <h2>{{ selected.name }}</h2>
        <div>
          <btn
            icon="trash"
            style="margin-right: 12px;"
            color="red"
            @click="$refs.commentDeleteDialog.open()"
          >{{ $t('btn.delete') }}</btn>
          <btn icon="save" @click="saveChanges">{{ $t('btn.save') }}</btn>
        </div>
      </div>
      <div>
        <r-form v-show="selected" ref="form"></r-form>
      </div>
    </div>
  </div>
</template>

<script>
import Form from '../components/CreationForm.vue'
import axios from '@/axios'
export default {
  data() {
    return {
      reactions: [],
      selected: null
    }
  },
  components: {
    'r-form': Form,
  },
  methods: {
    wrapReactionRow(row) {
      row.targetUuid = row.block_uuid
      row.useRegexp = row.use_regexp

      if (row.data.flags) {
        row.regexp = row.data
        row.textMatch = row.textMatch || {
          pattern: 'match me',
          caseSensitive: false
        }
      } else {
        row.regexp = {
          pattern: 'match me',
          flags: {
            g: false,
            m: false,
            i: false
          }
        }

        row.textMatch = row.data
      }

      delete row.use_regexp
      delete row.data
      delete row.block_uuid
    },

    async createReaction() {
      try {
        const formData = this.$refs.popupForm.getData()
        if (formData.useRegexp) delete formData.textMatch
        else delete formData.regexp
        const createReq = await axios.post('/api/plugin/comments/reactions/create', formData)
        const createdRow = createReq.data.created
        this.wrapReactionRow(createdRow)
        this.$refs.popupForm.reset()
        this.reactions.push(createdRow)
      } catch (e) {
        this.notify('error', 'plugins.comments.error.create')
        console.error(e)
      } finally {
        this.$refs.commentCreationDialog.close()
      }
    },

    isSelected(item) {
      if (item === this.selected) return true
    },

    setSelected(item) {
      this.selected = item
      this.$refs.form.load(JSON.parse(JSON.stringify(this.selected)))
    },

    async saveChanges() {
      try {
        const changed = this.$refs.form.getData()
        if (changed.useRegexp) delete changed.textMatch
        else delete changed.regexp
        const updateReq = await axios.put('/api/plugin/comments/reactions/update', changed)
        const updated = updateReq.data.updated
        this.wrapReactionRow(updated)
        for (let i = 0; i < this.reactions.length; i++) {
          if (this.reactions[i].id === this.selected.id) {
            this.$set(this.reactions, i, updated)
            this.selected = updated
            this.$refs.form.load(this.selected)
            break
          }
        }
        this.notify('success', 'plugins.comments.success.save')
      } catch (e) {
        this.notify('error', 'plugins.comments.error.save')
        console.error(e)
      }
    },

    async deleteReaction() {
      try {
        await axios.delete('/api/plugin/comments/reactions/delete', { data: { id: this.selected.id } })
        for (let i = 0; i < this.reactions.length; i++) {
          if (this.reactions[i].id === this.selected.id) {
            this.reactions.splice(i, 1)
            break
          }
        }
        this.selected = null
        this.notify('success', 'plugins.comments.success.delete')
      } catch (e) {
        this.notify('error', 'plugins.comments.error.delete')
        console.error(e)
      } finally {
        this.$refs.commentDeleteDialog.close()
      }
    }
  },

  async created() {
    try {
      await this.$store.dispatch('loadFlowData', this)
      const listReq = await axios.get('/api/plugin/comments/reactions/list')
      for (const r of listReq.data.reactions) this.wrapReactionRow(r)
      this.reactions = listReq.data.reactions
    } catch (e) {
      this.notify('error', 'plugins.comments.error.load')
      console.error(e)
    }
  }
}
</script>

<style lang="scss" scoped>
.comments-plugin {
  display: flex;
  height: 100%;

  &__top-buttons {
    @include alignedFlex(space-between);
    padding: 15px 20px 15px 0;
  }

  &__sidebar {
    @include boxShadow(-2px, 0px, 15px, -13px, rgba(0, 0, 0, 0.75));
    background-color: $bgLightGrey;
    width: 20vw;
    max-width: 350px;
    min-width: 310px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__items {
    overflow-y: scroll;
    height: 92%;
  }

  &__item {
    width: 92%;
    margin: 10px auto 10px auto;
  }

  &__create {
    @include clickableText();
  }
}
</style>
